<script>
import { showModal } from "@app/store/modalStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { commitHistory } from "@app/store/historyStore.svelte.js";
import Menu from "../menu/Menu.svelte";
import MenuItem from "../menu/MenuItem.svelte";
import MenuDivider from "../menu/MenuDivider.svelte";
import * as m from "@app/paraglide/messages.js";
import voiceUtils from "@cclib/providers/textToSpeechAll/voiceUtils.js";
import { isAudioGroupType } from "@app/lib/eventGroupTypes.js";

let hasSelection = $derived(
    Boolean(eventGroupState[projectState.selected]?.selected?.length > 0),
);
let hasEvents = $derived(
    Boolean(eventGroupState[projectState.selected]?.events?.length > 0),
);

async function trimToDuration() {
    const egIdx = projectState.selected;
    const selected = [...eventGroupState[egIdx].selected];

    commitHistory({
        name: "trim to duration",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                selected.forEach((eventIndex) => {
                    try {
                        const event = draft[egIdx].events[eventIndex];
                        if (event.audioFile && !isNaN(event.audioFileDuration)) {
                            event.end = event.start + voiceUtils.effectiveAudioDuration(event) + 0.5;
                        }
                    } catch (err) {
                        console.log(err.message, err);
                    }
                });
            },
        },
    });
}

function clearTimecodes() {
    const egIdx = projectState.selected;
    const selected = [...eventGroupState[egIdx].selected];

    commitHistory({
        name: "clear timecodes",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                selected.forEach((eventIndex) => {
                    try {
                        draft[egIdx].events[eventIndex].start = false;
                        draft[egIdx].events[eventIndex].end = false;
                    } catch (err) {
                        console.log(err.message, err);
                    }
                });
            },
        },
    });
}

function addOneFrame() {
    const egIdx = projectState.selected;
    const selected = [...eventGroupState[egIdx].selected];
    const frameDuration = 1 / projectState.frameRate;

    commitHistory({
        name: "add one frame",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                selected.forEach((eventIndex) => {
                    try {
                        if (draft[egIdx].events[eventIndex].end !== false) {
                            draft[egIdx].events[eventIndex].end += frameDuration;
                        }
                    } catch (err) {
                        console.log(err.message, err);
                    }
                });
            },
        },
    });
}

function subtractOneFrame() {
    const egIdx = projectState.selected;
    const selected = [...eventGroupState[egIdx].selected];
    const frameDuration = 1 / projectState.frameRate;

    commitHistory({
        name: "subtract one frame",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                selected.forEach((eventIndex) => {
                    try {
                        const event = draft[egIdx].events[eventIndex];
                        if (event.end !== false && event.start !== false) {
                            const newEnd = event.end - frameDuration;
                            if (newEnd > event.start) {
                                event.end = newEnd;
                            }
                        }
                    } catch (err) {
                        console.log(err.message, err);
                    }
                });
            },
        },
    });
}
</script>

<Menu id="timecode" label={m.timecode()}>
    <MenuItem
        label={m.project_settings()}
        icon="bi-gear"
        id="projectSettingsBtn"
        onSelect={() => showModal("projectTimingSettings")} />
    <MenuItem
        label={m.convert_timecode()}
        icon="bi-arrow-left-right"
        id="convertTimecodeBtn"
        onSelect={() => showModal("convertTimecode")} />
    <MenuItem
        label={m.timecode_offset()}
        icon="bi-stopwatch"
        id="tcOffsetBtn"
        shortcut="Offset Timecode"
        disabled={!hasSelection}
        disabledReason={m.no_events_selected()}
        onSelect={() => showModal("tcOffset")} />
    <MenuItem
        label={m.stretch_shrink()}
        icon="bi-arrows-expand"
        id="timeStretchBtn"
        disabled={!hasSelection}
        disabledReason={m.no_events_selected()}
        onSelect={() => showModal("timeStretch")} />
    <MenuItem
        label={m.auto_correct_reading_speed()}
        icon="bi-speedometer"
        id="autoCorrectBtn"
        disabled={!hasEvents}
        onSelect={() => showModal("autoCorrectTiming")} />
    <MenuItem
        label={m.copy_tc_from_group()}
        icon="bi-copy"
        id="copyTcBtn"
        disabled={eventGroupState.length < 2}
        onSelect={() => showModal("copyTcFromGroup")} />
    {#if isAudioGroupType(eventGroupState[projectState.selected]?.type)}
        <MenuItem
            label={m.trim_to_duration()}
            icon="bi-scissors"
            id="trimDurationBtn"
            shortcut="Trim To Audio Duration"
            disabled={!hasSelection}
            disabledReason={m.no_events_selected()}
            onSelect={trimToDuration} />
    {/if}
    <MenuDivider />
    <MenuItem
        label={m.subtract_one_frame()}
        icon="bi-dash"
        id="subtractOneFrameBtn"
        shortcut="Subtract 1 Frame From End"
        disabled={!hasSelection}
        disabledReason={m.no_events_selected()}
        onSelect={subtractOneFrame} />
    <MenuItem
        label={m.add_one_frame()}
        icon="bi-plus"
        id="addOneFrameBtn"
        shortcut="Add 1 Frame To End"
        disabled={!hasSelection}
        disabledReason={m.no_events_selected()}
        onSelect={addOneFrame} />
    <MenuDivider />
    <MenuItem
        label={m.clear_timecodes()}
        icon="bi-eraser"
        id="clearTcBtn"
        shortcut="Clear Timecodes"
        disabled={!hasSelection}
        disabledReason={m.no_events_selected()}
        onSelect={clearTimecodes} />
</Menu>
